import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Divider, Grid } from "@mui/material";
import { FormikProps } from "formik";

import { AddressFields } from "../../../../../../address-fields/AddressFields";
import { ClientInsuranceNumberField } from "../../../../../../client-insurance-number/ClientInsuranceNumberField";
import { ClientPersonalDataMaxLength } from "../../../../../../constants/clientPersonalDataMaxLength";
import { ContentCard } from "../../../../../../content-card/ContentCard";
import { FormikTextField } from "../../../../../../formik/FormikTextField";
import { FormikAutocomplete } from "../../../../../../formik/formik-autocomplete/FormikAutocomplete";
import { toOptionItems } from "../../../../../../formik/formik-autocomplete/FormikAutocomplete.utils";
import { FormikAutocompleteSelect } from "../../../../../../formik/formik-autocomplete/FormikAutocompleteSelect";
import { FormikDatePicker } from "../../../../../../formik/formik-date-picker/FormikDatePicker";
import { DropdownOptions } from "../../../../../../hooks/useDropdownOptions";
import { AssetInventoryMaxLength, AssetPersonalDataFormValues } from "../../AssetPersonalData.types";
import { FieldWithInfo } from "../field-with-info/FieldWithInfo";

interface Props {
  formik: FormikProps<AssetPersonalDataFormValues>;
  dropdownOptions: DropdownOptions;
}

export function PersonBlock({ formik, dropdownOptions }: Props) {
  const { t } = useTranslation();
  const employmentStatusOptions = useMemo(
    () => toOptionItems(dropdownOptions.employmentStatuses),
    [dropdownOptions.employmentStatuses]
  );

  return (
    <ContentCard title={t("clients.client.debtSettlement.assetInventory.section1.personalDataTitle")}>
      <Grid container rowSpacing={2} columnSpacing={2}>
        <Grid size={4}>
          <FormikAutocomplete
            formik={formik}
            fieldName="salutation"
            label={t("clients.create.salutation")}
            options={dropdownOptions.salutations}
            required
          />
        </Grid>
        <Grid size={4}>
          <FormikAutocomplete
            formik={formik}
            fieldName="title"
            label={t("clients.create.title")}
            options={dropdownOptions.titles}
            freeSolo
          />
        </Grid>
        <Grid size={4} />

        <Grid size={4}>
          <FormikTextField
            formik={formik}
            fieldName="firstName"
            label={t("clients.create.name")}
            required
            slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.firstName } } }}
          />
        </Grid>
        <Grid size={4}>
          <FormikTextField
            formik={formik}
            fieldName="lastName"
            label={t("clients.create.lastName")}
            required
            slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.lastName } } }}
          />
        </Grid>
        <Grid size={4}>
          <FormikTextField
            formik={formik}
            fieldName="formerLastName"
            label={t("clients.create.formerLastName")}
            slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.formerLastName } } }}
          />
        </Grid>

        <Grid size={4}>
          <ClientInsuranceNumberField formik={formik} />
        </Grid>
        <Grid size={4}>
          <FormikDatePicker formik={formik} fieldName="birthday" label={t("clients.create.birthday")} />
        </Grid>
        <Grid size={4}>
          <FieldWithInfo
            fieldLabel={t("clients.client.masterData.personalData.placeOfBirth")}
            info={t("clients.client.debtSettlement.assetInventory.section1.placeOfBirthInfo")}
          >
            <FormikTextField
              formik={formik}
              fieldName="placeOfBirth"
              label={t("clients.client.masterData.personalData.placeOfBirth")}
              slotProps={{ input: { inputProps: { maxLength: ClientPersonalDataMaxLength.placeOfBirth } } }}
            />
          </FieldWithInfo>
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <AddressFields
          formik={formik}
          streetField="street"
          houseNumberField="houseNumber"
          zipCodeField="zipCode"
          cityField="city"
          countryField="country"
          streetMaxLength={ClientPersonalDataMaxLength.street}
          required={{ street: true, houseNumber: true, zipCode: true, city: true, country: true }}
        />

        <Grid size={12}>
          <FormikTextField
            formik={formik}
            fieldName="phoneNumber"
            label={t("clients.create.phone")}
            slotProps={{ input: { inputProps: { maxLength: AssetInventoryMaxLength.phoneNumber } } }}
          />
        </Grid>

        <Grid size={12}>
          <Divider />
        </Grid>

        <Grid size={12}>
          <FormikAutocompleteSelect
            formik={formik}
            fieldName="employmentStatus"
            label={t("clients.client.debtSettlement.assetInventory.section1.beruf")}
            options={employmentStatusOptions}
            required
          />
        </Grid>
      </Grid>
    </ContentCard>
  );
}
